import { getSupabaseClient } from "@veorest/api-client";
import type { TranslationKey } from "../i18n";
import { listPartnerOrders, type PartnerOrderRow } from "./partnerOrders";
import { listPartnerDrivers, type PartnerDriverRow } from "./partnerDrivers";

export interface PartnerClientRow {
  id: string;
  name: string;
  slug: string;
  /** Client login username (login ID) — reserved at creation. */
  username: string | null;
  /** Owner contact email — optional, for invoices later (never a login ID). */
  owner_email: string | null;
  tenant_status: string;
  phone: string | null;
  address: string | null;
  logo_url: string | null;
  currency: string;
  client_type: string;
  controlling_partner_id: string | null;
  link_status: string | null;
  dispatch_authority: string | null;
  total_orders_count: number;
  active_orders_count: number;
  active_drivers_count: number;
  last_order_at: string | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function str(v: unknown, fallback = ""): string {
  if (v == null) return fallback;
  return String(v);
}

function num(v: unknown, fallback = 0): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function mapPartnerClient(raw: Record<string, unknown>): PartnerClientRow {
  return {
    id: str(raw.id),
    name: str(raw.name, "Business"),
    slug: str(raw.slug),
    username: raw.username != null ? str(raw.username) : null,
    owner_email: raw.owner_email != null ? str(raw.owner_email) : null,
    tenant_status: str(raw.tenant_status, "unknown"),
    phone: raw.phone != null ? str(raw.phone) : null,
    address: raw.address != null ? str(raw.address) : null,
    logo_url: raw.logo_url != null ? str(raw.logo_url) : null,
    currency: str(raw.currency, "IQD"),
    client_type: str(raw.client_type, ""),
    controlling_partner_id:
      raw.controlling_partner_id != null ? str(raw.controlling_partner_id) : null,
    link_status: raw.link_status != null ? str(raw.link_status) : null,
    dispatch_authority:
      raw.dispatch_authority != null ? str(raw.dispatch_authority) : null,
    total_orders_count: num(raw.total_orders_count),
    active_orders_count: num(raw.active_orders_count),
    active_drivers_count: num(raw.active_drivers_count),
    last_order_at: raw.last_order_at != null ? str(raw.last_order_at) : null,
  };
}

/**
 * Partner clients from hybrid RPC (SoT identity OR legacy links).
 * Trust RPC result; do not fail-close identity-only (legacy rows still valid).
 */
export async function listPartnerClients(
  partnerId: string
): Promise<PartnerClientRow[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_list_tenants", {
    p_partner_id: partnerId,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const list = Array.isArray(row.tenants) ? row.tenants : [];
  return list
    .map((item) => asRecord(item))
    .filter((r): r is Record<string, unknown> => !!r)
    .map(mapPartnerClient)
    .filter((c) => {
      // Drop only clear cross-partner SoT mismatches (never show other partners' clients).
      if (
        c.controlling_partner_id &&
        c.controlling_partner_id !== partnerId &&
        c.client_type === "partner"
      ) {
        return false;
      }
      return Boolean(c.id);
    });
}

export async function createPartnerClient(
  partnerId: string,
  input: {
    name: string;
    phone: string;
    address: string;
    username: string;
    email?: string;
  }
): Promise<{ passcode: string | null; tenantId: string | null }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_create_business_onboarding", {
    p_partner_id: partnerId,
    p_name: input.name.trim(),
    p_phone: input.phone.trim(),
    p_address: input.address.trim(),
    p_email: input.email?.trim() || null,
    p_username: input.username.trim().toLowerCase(),
    p_expiry_minutes: 120,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const tenantObj = asRecord(row.tenant);
  return {
    passcode: row.passcode != null ? str(row.passcode) : null,
    tenantId:
      row.tenant_id != null
        ? str(row.tenant_id)
        : tenantObj?.id != null
          ? str(tenantObj.id)
          : null,
  };
}

/**
 * Re-issue the onboarding passcode for a client still in `onboarding` status.
 * Deactivates the previous active code and mints a fresh single-use one for the
 * SAME tenant (never creates a second client).
 */
export async function reissuePartnerClientPasscode(
  partnerId: string,
  tenantId: string
): Promise<{ passcode: string | null; tenantId: string | null }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc(
    "reissue_business_onboarding_passcode",
    {
      p_tenant_id: tenantId,
      p_partner_id: partnerId,
      p_expiry_minutes: 120,
    }
  );
  if (error) throw error;
  const row = asRecord(data) ?? {};
  return {
    passcode: row.passcode != null ? str(row.passcode) : null,
    tenantId: row.tenant_id != null ? str(row.tenant_id) : null,
  };
}

export async function updatePartnerClient(
  partnerId: string,
  tenantId: string,
  patch: {
    name?: string;
    phone?: string;
    address?: string;
    username?: string;
    email?: string | null;
  }
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("partner_update_client", {
    p_partner_id: partnerId,
    p_tenant_id: tenantId,
    p_patch: patch,
  });
  if (error) throw error;
}

export async function deletePartnerClient(
  partnerId: string,
  tenantId: string
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("partner_delete_client", {
    p_partner_id: partnerId,
    p_tenant_id: tenantId,
  });
  if (error) throw error;
}

export async function listPartnerClientOrders(
  partnerId: string,
  tenantId: string
): Promise<PartnerOrderRow[]> {
  const result = await listPartnerOrders(partnerId, {
    tenantId,
    limit: 50,
  });
  return result.orders;
}

/** Partner-fleet drivers currently assigned on this client's open orders. */
export async function listPartnerClientDriverSummary(
  partnerId: string,
  tenantId: string
): Promise<PartnerDriverRow[]> {
  const [orders, drivers] = await Promise.all([
    listPartnerClientOrders(partnerId, tenantId),
    listPartnerDrivers(partnerId),
  ]);
  const byId = new Map(drivers.map((d) => [d.id, d]));
  const assigned = new Map<string, PartnerDriverRow>();
  for (const order of orders) {
    if (order.stage === "completed" || order.stage === "cancelled") continue;
    const id = order.assigned_driver_id;
    if (!id) continue;
    const driver = byId.get(id);
    if (!driver) continue; // never surface OELOX / non-partner fleet
    assigned.set(id, driver);
  }
  return [...assigned.values()];
}

export function clientStatusLabel(status: string): string {
  const s = status.toLowerCase();
  if (s === "active" || s === "open" || s === "live") return "Active";
  if (s === "inactive" || s === "closed" || s === "suspended") return "Inactive";
  if (s === "pending") return "Pending";
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : "Unknown";
}

export function formatLastOrderAt(iso: string | null): string {
  if (!iso) return "No activity yet";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "No activity yet";
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/** Map client RPC failures to i18n keys — call `t(mapClientsRpcError(err))` at the UI. */
export function mapClientsRpcError(err: unknown): TranslationKey {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  const lower = msg.toLowerCase();
  if (lower.includes("username_taken")) return "errors.usernameTaken";
  if (lower.includes("username_reserved")) return "errors.usernameInvalid";
  if (lower.includes("username_invalid") || lower.includes("username_required")) {
    return "errors.usernameInvalid";
  }
  if (lower.includes("client_not_found")) return "errors.clientNotFound";
  if (lower.includes("not_allowed") || lower.includes("permission")) {
    return "errors.forbidden";
  }
  return "errors.requestFailed";
}

/** @deprecated Prefer `mapClientsRpcError` + `t(...)`. */
export function clientsRpcErrorMessage(err: unknown): string {
  void err;
  return "Request failed.";
}
